import { IRichTextContent } from "@aptedge/lib-ui/src/components/TextEditor/TextEditor";
import { IDatalayer } from "@aptedge/lib-ui/src/utils/event";
import { UseMutationResult } from "@tanstack/react-query";
import { DateTime } from "luxon";
import type React from "react";
import { IconName } from "../components/GMIcon/iconNames";
import { LayoutRegistry } from "../features/FeedLayout/layouts";
import { AnswerAction } from "../redux/reduxSlice/answerGPTSlice";
import { ContentType } from "./http";

export enum AppType {
  WEB_APP = "WEB_APP",
  SUPPORT_APP = "SUPPORT_APP"
}
export interface ImageUploadResult {
  success: boolean;
  name?: string;
  url?: string;
}
export type NextStep = {
  text: string;
  icon?: IconName;
  is_primary?: boolean;
  run?: string;
  frontend?: boolean;
};
export enum QUERY_PARAMS {
  RESULT_ID = "resultId",
  PAGE = "page",
  ANSWER_ID = "answerId",
  ARTICLE_ID = "articleId",
  ARTICLE_CREATION_STATE = "articleCreationState",
  ARTICLE_STATUS = "articleStatus",
  TRIGGER = "trigger",
  SHOW_PREVIEW = "showPreview",
  OPEN_ARTICLE_DRAWER = "openArticleDrawer",
  QUESTION_INDEX = "questionIndex",
  READ_ONLY = "readOnly"
}
export enum SEARCH_RESULT_TYPE {
  EDGE = "edge",
  TICKET = "ticket",
  ISSUE = "issue",
  ARTICLE = "article",
  SOCIAL = "social",
  DOCUMENT = "document",
  FEDERATED = "federated",
  GENERATED_KNOWLEDGE = "generated_knowledge",
  ANSWERAI_FEEDBACK = "answerai_feedback"
}
export enum ApiChatMessageSender {
  SYSTEM = "SYSTEM",
  AI = "AI",
  HUMAN = "HUMAN"
}
export enum ApiChatStatus {
  IDLE = "IDLE",
  IN_PROGRESS = "IN_PROGRESS",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR"
}
export enum ApiActionType {
  Answer = "Answer",
  Search = "Search",
  Template = "Template",
  SQL = "SQL",
  RefreshTicket = "RefreshTicket",
  CreateArticle = "CreateArticle",
  Frontend = "Frontend",
  Filter = "Filter",
  MCP = "MCP"
}

export type ApiActionLogMessage = {
  message: string;
  level: string;
  thread: string;
  timestamp: number;
};

export type ApiActionDoc = {
  display_ids: string[];
  group: { name: string; type: string };
};

export type ApiActionSearchHit = {
  breadcrumb: { levels: string[] };
  chunk: string | null;
  chunk_id: string | null;
  chunk_offset_start: number | null;
  chunk_offset_end: number | null;
  doc: ApiActionDoc;
  evidence: string[];
  hit_type: string;
  rank: number;
  search_rank: number;
  search_score: number;
  similarity_score: number;
};

export type ApiActionSearchChunk = {
  id: string;
  group_name: string;
  group_type: string;
  url: string;
  preview: string;
  content: string;
  start_offset: number | null;
  end_offset: number | null;
  hit_offsets: number[][];
  answer_excluded: boolean;
};

export interface ApiStreamingJSONAnswerSource {
  title?: string;
  url?: string;
  pages?: number[];
  id?: string;
  type?: string;
  displayId?: string;
  displayUrl?: string;
}

export interface ApiActionOutputDataQA {
  summary?: string;
  details?: string;
  sources?: ApiStreamingJSONAnswerSource[];
}

export interface ApiActionOutputDataTicketInsights {
  title?: string;
  ticket_id?: number;
  ticket_display_id?: string;
  ticket_labels?: {
    icon: string;
    text: string;
  }[];
  ticket_last_updated?: string;
}

export interface ApiActionOutputDataKBDraftArticle {
  title?: string;
  body?: string;
}

export interface ApiActionOutputDataKBDraft {
  articles?: ApiActionOutputDataKBDraftArticle[];
}

export interface ApiActionOutputDataSessionReplay {
  description?: string;
}

// TODO: Make these subtypes mutually-exclusive, instead of union-ing all of their fields together
export interface ApiActionOutputData
  extends
    ApiActionOutputDataQA,
    ApiActionOutputDataTicketInsights,
    ApiActionOutputDataKBDraft,
    ApiActionOutputDataSessionReplay {
  array?: string[];
  text?: string;
  layout?: string;
  followups?: NextStep[];
}

export interface ApiActionOutputInternal {
  thoughts?: string;
  observations?: string;
}

export interface ApiActionOutput {
  data: ApiActionOutputData;
  internal: ApiActionOutputInternal;
  next?: { children: AnswerAction[]; group_type: "Sequence" | "Parallel" };
}

export interface ApiAction {
  id: string;
  action_type: ApiActionType;
  description: string | null;
  output: ApiActionOutput | null;
  visible: boolean;
  streaming: boolean;
  status: ApiChatStatus;
  args?: Record<string, unknown>;
  config?: Record<string, unknown>;
  query?: string | null;
  template?: string | null;
  search_hits?: ApiActionSearchHit[];
  search_chunks?: ApiActionSearchChunk[];
  log_messages?: ApiActionLogMessage[];
  prompt?: string;
  timers?: Record<string, number>;
}

// Initial fix to avoid looping through all actions just to extract data
export interface FlatAction {
  summary?: string;
  details?: string;
  text?: string;
  sources?: ApiStreamingJSONAnswerSource[];
  followups?: NextStep[];
  title?: string;
  description?: string;
  layout?: string;
  ticket_id?: number;
  ticket_display_id?: string;
  ticket_labels?: {
    icon?: string;
    text?: string;
  }[];
  ticket_last_updated?: string;
}
export interface SearchArgs {
  apt_answer_filters: {
    product_filters: string | null;
    quick_filters: string | null;
    // The products picked for the question (comma-joined, like product_filters), when the asker
    // selected none.
    detected_product_filters?: string | null;
  };
  detect_ids_in_query: boolean;
  is_self_service: boolean;
  limit: number;
  log_fields: {
    answerId: string;
  };
  rerank: boolean;
  sort: string;
  sources: Record<string, string[] | string>;
  strategy: string;
  ticket_search_window: string;
}

export type FeedLayoutType = keyof LayoutRegistry;
export interface ApiQuestion {
  id?: string;
  actions: ApiAction[];
  query: string;
  layout: FeedLayoutType;
  root: string;
  search_args: SearchArgs;
  search_results: ICompositeResult[];
  stats: Record<string, number>;
  status: ApiChatStatus;
  actions_repo_branch: string | null;
  actions_repo_commit: string | null;
  created_at: number | null;
  created_by: string | null;
  // The case filed from this answer, if any. It rides the question rather
  // than the answer so its card renders in sequence in the feed, and so a
  // conversation that files several cases keeps them apart.
  linked_ticket?: LinkedTicket | null;
}

/** The Salesforce Contact a case was filed for. */
export interface TicketReporter {
  contact_id?: string;
  name?: string;
  email?: string;
  account_id?: string;
}

/** The Salesforce case filed from a conversation. */
export interface LinkedTicket {
  // Null until the case has been ingested as an internal ticket - which never
  // happens for a tenant that doesn't ingest from Salesforce. Presence of the
  // record itself is what says a case was filed.
  ticket_id: number | null;
  case_number?: string | null;
  // The Salesforce case id (500...) when there is no ingested ticket yet.
  external_id?: string | null;
  subject?: string | null;
  status?: string | null;
  created_on?: number | null;
  reporter?: TicketReporter | null;
  // Deep link into Salesforce, when the instance URL is known.
  case_url?: string | null;
  // The form values the case was filed with, as submitted. What the card's
  // "Ticket details" toggle shows on a later visit.
  fields?: Record<string, string> | null;
}

export interface ApiAnswer {
  questions: ApiQuestion[];
  timers: Record<string, unknown>;
}

export interface GQADocSearchResult extends PaginatedCompositeSearchResult {
  answer?: ApiAnswer;
}

declare global {
  interface Window {
    // Window layer is controlled on GTM, so typecheck is not feasible here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aptedgeEvents: IDatalayer<any>[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    posthog: any;
  }
}

// Base Entity
export interface IEntity {
  id: number;
  name: string;
}

export enum EntityType {
  PRODUCT = "product",
  THEME = "theme",
  CUSTOMER = "customer",
  FEATURE = "feature"
}

// CUSTOMER omitted from LabelEntity by design
const { CUSTOMER, ...LabelEntity } = EntityType;
export { LabelEntity };
export type LabelEntityType = Exclude<EntityType, "customer">;

export enum MetricCategory {
  PRODUCT = "product",
  THEME = "theme",
  STATUS = "status",
  TEAM = "team",
  ASSIGNEE = "assignee",
  CUSTOMER = "customer",
  FEATURE = "feature",
  EDGE = "edge",
  JIRA = "jira"
}

export interface IMetricsCategoryItem {
  id: number;
  name: string;
  parentName?: string | null;
  change: number;
  total: number;
  type: MetricCategory;
  age?: number;
}

export interface EntityFilters {
  products: number[];
  features: number[];
  themes: number[];
  customers: number[];
}

// Business Data Source

export enum BusinessDataTaskStatus {
  UNKNOWN = "unknown",
  PENDING = "pending",
  SUCCESSFUL = "successful",
  FAILED = "failed"
}

export enum BusinessDataSourceType {
  SALESFORCE = "SALESFORCE"
}

// Customers

export interface IOriginalCustomer {
  id: number;
  name: string;
  pinned: boolean;
  value: { amount: number };
}

export interface ICustomer extends IEntity {
  pinned: boolean;
  monetaryValue: number;
}

export interface IOriginalCustomerListItem extends IOriginalCustomer {
  ticketCount: number;
}

export interface ICustomerListItem extends ICustomer {
  ticketCount: number;
}

// Edges

export enum EdgeAuditLogType {
  EDGE_ARCHIVING = "EDGE_ARCHIVING",
  EDGE_ASSIGNEE_CHANGE = "EDGE_ASSIGNEE_CHANGE",
  EDGE_TEAM_CHANGE = "EDGE_TEAM_CHANGE",
  EDGE_SPACE_CHANGE = "EDGE_SPACE_CHANGE",
  EDGE_COMMENT_CHANGE = "EDGE_COMMENT_CHANGE",
  EDGE_CREATION = "EDGE_CREATION",
  EDGE_EXAMPLE_CHANGE = "EDGE_EXAMPLE_CHANGE",
  EDGE_FILE_CHANGE = "EDGE_FILE_CHANGE",
  EDGE_INTERNAL_ISSUE_REFERENCE_CHANGE = "EDGE_INTERNAL_ISSUE_REFERENCE_CHANGE",
  EDGE_LABEL_CHANGE = "EDGE_LABEL_CHANGE",
  EDGE_MERGING = "EDGE_MERGING",
  EDGE_MIGRATION = "EDGE_MIGRATION",
  EDGE_NAME_CHANGE = "EDGE_NAME_CHANGE",
  EDGE_SUMMARY_CHANGE = "EDGE_SUMMARY_CHANGE",
  EDGE_STATUS_TRANSITION = "EDGE_STATUS_TRANSITION",
  EDGE_WEBLINK_CHANGE = "EDGE_WEB_LINK_CHANGE"
}

export type IBaseAuditLog = {
  id: number;
  authorId: number | null;
  createdOn: number;
  displayText: string;
  edgeId: number;
  edgeDisplayName: string;
};

export type IEdgeAuditLogArchiving = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_ARCHIVING;
  data: { archived: boolean };
};

export type IEdgeAuditLogAssigneeChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_ASSIGNEE_CHANGE;
  data: {
    newUser?: { id: number; email: string };
    oldUser?: { id: number; email: string };
  };
};

export type IEdgeAuditLogCommentChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_COMMENT_CHANGE;
  data: {
    added: { newText: IRichTextContent; id: number }[];
    edited: {
      newText: IRichTextContent;
      oldText: IRichTextContent;
      id: number;
    }[];
    deleted: { oldText: IRichTextContent; id: number }[];
  };
};

export type IEdgeAuditLogCreation = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_CREATION;
  data: { name: string };
};

export type IEdgeAuditLogExampleChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_EXAMPLE_CHANGE;
  data: {
    added: { id: number; externalId: string; displayId?: string }[];
    removed: { id: number; externalId: string; displayId?: string }[];
  };
};

export type IEdgeAuditLogFileChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_FILE_CHANGE;
  data: {
    added?: { fileId: string; fileName: string; contentType: ContentType }[];
    removed?: { fileId: string; fileName: string; contentType: ContentType }[];
  };
};

export type IEdgeAuditLogInternalIssueChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_INTERNAL_ISSUE_REFERENCE_CHANGE;
  data: {
    added: { id: number; externalId: string }[];
    removed: { id: number; externalId: string }[];
  };
};

type LabelAuditData = {
  added: { id: number; displayName: string }[];
  removed: { id: number; displayName: string }[];
};

export type IEdgeAuditLogLabelChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_LABEL_CHANGE;
  data: {
    [EntityType.PRODUCT]?: LabelAuditData;
    [EntityType.FEATURE]?: LabelAuditData;
    [EntityType.THEME]?: LabelAuditData;
  };
};

export type IEdgeAuditLogMerging = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_MERGING;
  data: {
    from?: { id: number; displayName: string }[];
    to?: { id: number; displayName: string }[];
  };
};

export type IEdgeAuditLogMigration = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_MIGRATION;
  data: {
    from?: { id: number; displayName: string };
    to?: { id: number; displayName: string };
    ticketIds: number[];
  };
};

export type IEdgeAuditLogNameChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_NAME_CHANGE;
  data: { oldText: string; newText: string };
};

export type IEdgeAuditLogSummaryChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_SUMMARY_CHANGE;
  data: { oldText?: IRichTextContent; newText?: IRichTextContent };
};

export type IEdgeAuditLogTeamChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_TEAM_CHANGE;
  data: {
    currentTeam?: ITeam;
    previousTeam?: ITeam;
  };
};

export type IEdgeAuditLogSpaceChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_SPACE_CHANGE;
  data: {
    currentSpace?: string;
    previousSpace?: string;
  };
};

export type IEdgeAuditLogStatusChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_STATUS_TRANSITION;
  data: { previous: IEdgeStatus; current: IEdgeStatus };
};

export type IEdgeAuditLogWeblinkChange = IBaseAuditLog & {
  type: EdgeAuditLogType.EDGE_WEBLINK_CHANGE;
  data: { added: string[]; removed: string[] };
};

export type IEdgeAuditLog =
  | IEdgeAuditLogArchiving
  | IEdgeAuditLogAssigneeChange
  | IEdgeAuditLogCommentChange
  | IEdgeAuditLogCreation
  | IEdgeAuditLogExampleChange
  | IEdgeAuditLogFileChange
  | IEdgeAuditLogInternalIssueChange
  | IEdgeAuditLogLabelChange
  | IEdgeAuditLogMerging
  | IEdgeAuditLogMigration
  | IEdgeAuditLogNameChange
  | IEdgeAuditLogStatusChange
  | IEdgeAuditLogSummaryChange
  | IEdgeAuditLogTeamChange
  | IEdgeAuditLogSpaceChange
  | IEdgeAuditLogWeblinkChange;

export interface IEdgeListing {
  id: number;
  name: string;
  displayName: string;
  internalIssues: IProductIssue[];
  tickets: Pick<ITicketListing, "id" | "externalId">[];
  links: IEdgeLink[];
  summary: string | null;
  richSummary: IRichTextContent | null;
  pointOfContact: string | null;
  assignee: IUserListing | null;
  comments: IEdgeComment[];
  labels: ILabel[];
  status: IEdgeStatus | null;
  createdOn: number;
  createdBy: IUserListing | null;
  updatedOn: number;
  userUpdatedOn: number | null;
  userUpdatedBy: IUserListing | null;
  archived: boolean;
  team: ITeam | null;
  space: string | undefined;
  filters: number[];
  ticketCount?: number;
  isAutoreplyEnabled: boolean;
  isAutolinkEnabled: boolean;
  preview?: string;
  url: string;
  offset: ResultOffsetType;
  language: string;
  score_details?: ScoreDetails;
}

export interface IEdgeInfo extends IEdgeListing {
  links: IEdgeLink[];
  usersWatching: number[];
  status: IEdgeStatus;
  internalIssues: IProductIssueDetailed[];
}

export type IEdgeResult = IEdgeListing & { type: "edge" };
export type ITicketResult = ITicketListing & { type: "ticket" };
export type IArticleResult = IArticle & { type: "article" };
export type IFederatedResult = IFederated & { type: "federated" };
export type IDocumentResult = IDocument & { type: "document" };
export type IIssueResult = IIssueListing & { type: "issue" };
export type IGeneratedKnowledgeResult = IGeneratedKnowledgeListing & {
  type: "generated_knowledge";
};
export type IAnswerAIFeedbackResult = IAnswerAIFeedback & {
  type: "answerai_feedback";
};

export type ICompositeResult =
  | IEdgeResult
  | ITicketResult
  | IArticleResult
  | IFederatedResult
  | IIssueResult
  | ISocialResult
  | IDocumentResult
  | IGeneratedKnowledgeResult
  | IAnswerAIFeedbackResult;

export interface PaginatedSearchResultInfo {
  hasNext: boolean;
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  timers: Record<string, unknown>;
}
export interface PaginatedCompositeSearchResult extends PaginatedSearchResultInfo {
  items: ICompositeResult[];
}

export type IEdgeSearchResult = Pick<
  IEdgeListing,
  | "id"
  | "name"
  | "richSummary"
  | "labels"
  | "createdOn"
  | "archived"
  | "status"
  | "ticketCount"
  | "updatedOn"
  | "offset"
>;

export type IJobStatus = "pending" | "running" | "success" | "failed";

export type IBulkReplyFilters = {
  status?: string[]; // The user can set a status filter so that only tickets with that/those status(es) will receive the reply.
};

export type IBulkReply = {
  /** Airflow job ID */
  runId: string;
  edgeId: number;
  /** Timestamp when the reply was initialized */
  createdOn: number;
  /** Timestamp when the reply was sent */
  completedOn?: number;
  createdBy: Pick<IUser, "id" | "email">;
  status: IJobStatus;
  filters: IBulkReplyFilters | undefined;
  content: string;
  internal: boolean;
};

export interface ILabel {
  id: number;
  name: string;
  parentName?: string | null;
  type: EntityType;
}

export interface IEdgeComment {
  createdOn: number;
  updatedOn: number;
  richText: IRichTextContent | null;
  id: number;
  user: IUserListing;
  edgeId: number;
}

export enum IEdgeStatus {
  NEW = "NEW",
  REVIEW = "REVIEW",
  CONFIRMED = "CONFIRMED",
  ESCALATED = "ESCALATED",
  RESOLVED = "RESOLVED"
}

// Edge Resources (Files / Links)
export interface IEdgeLink {
  url: string;
  label?: string | null;
  urlReference: number;
}

export interface IEdgeFile {
  id: string;
  name: string;
  contentType: ContentType;
}

// Features

export interface IFeature extends IEntity {
  productId: number;
  productName: string;
  rule: IRule | null;
}

// Impact

export interface IBaseImpact {
  customerCount?: number;
  ticketCount: number;
  monetaryValue?: number;
}

export interface IImpactByCustomer extends IBaseImpact {
  customer: number;
  customerName: string;
}

export interface IImpactByProduct extends IBaseImpact {
  product: number;
  productName: string;
}

export interface IImpactByFeature extends IBaseImpact {
  feature: number;
  featureName: string;
}

export interface IImpactByTheme extends IBaseImpact {
  theme: number;
  themeName: string;
}

export interface IRawImpact extends IBaseImpact {
  product?: number;
  productName?: string;
  feature?: number;
  featureName?: string;
  customer?: number;
  customerName?: string;
  theme?: number;
  themeName?: string;
}

export interface IEnrichedImpact {
  productName?: string;
  featureName?: string;
  customerName?: string;
  themeName?: string;
}

export type IImpact = IRawImpact & IEnrichedImpact;

// Internal Issues

export interface IInternalIssue {
  id: number;
  externalId: string;
}

export enum InternalIssueSourceType {
  MOCK = "MOCK",
  JIRA = "JIRA"
}

export interface IInternalIssueSource extends IProviderSource {
  type: InternalIssueSourceType;
}

export type IJiraCloudInternalIssueSource = IProviderSource;

export interface IJiraOnPremiseInternalIssueSource {
  accessToken: string;
  configuredBaseUrl: string;
  status: IJiraOnPremStatus;
}

export interface ICreateJiraOnPremiseInternalIssueSourceRequest {
  configuredBaseUrl: string;
  accessToken: string;
}

export type ICreateJiraCloudInternalIssueSourceRequest = Pick<
  IJiraCloudInternalIssueSource,
  "oauthClientId" | "oauthClientSecret" | "configuredBaseUrl"
> & { id?: number };

export type IConfluenceCloudArticleSource = IProviderSource;

export interface ISharepointPagesSource {
  id: number;
  instanceName: string;
  name: string;
  providerUserLogin: boolean;
  status: string;
  tenantId: string;
  type: string;
}

export interface ISharepointDriveSource {
  id: number;
  instanceName: string;
  name: string;
  providerUserLogin: boolean;
  status: string;
  tenantId: string;
  type: string;
  sites?: ISharepointDriveSite[];
}

export interface ISharepointDriveSite {
  siteId: string;
  error?: string;
  siteName: string;
  siteUrl: string;
}

export type ICreateSharepointPagesSourceRequest = Pick<
  ISharepointPagesSource,
  "tenantId"
>;

export type ICreateSharepointDriveSourceRequest = Pick<
  ISharepointPagesSource,
  "tenantId"
>;

export interface IConfluenceOnPremArticleSource {
  configuredBaseUrl: string;
  accessToken: string;
  status: string;
}

export interface IZendeskKBArticleSource {
  zendeskInstanceName: string;
  accessToken: string;
  username: string;
  status: string;
  type: string;
  id: string;
  providerUserLogin: string;
  name: string;
}

export interface IZendeskKBArticleSourceRequest {
  zendeskInstanceName: string;
  accessToken: string;
  username: string;
}

export type ICreateConfluenceCloudArticleSourceRequest = Pick<
  IConfluenceCloudArticleSource,
  "oauthClientId" | "oauthClientSecret"
>;

export type ICreateConfluenceOnPremArticleSourceRequest = Pick<
  IConfluenceOnPremArticleSource,
  "configuredBaseUrl" | "accessToken"
>;

export type ICreateOneloginSourceRequest = {
  url: string;
  issuerUrl: string;
  certificate: string;
};

export type IOneloginSource = {
  url?: string;
  issuerUrl?: string;
  certificate?: string;
};

export type ICreateOktaSourceRequest = {
  oktaDomain: string;
  oktaClientId: string;
  oktaSecretKey: string;
};

export type IOktaSource = {
  oktaDomain?: string;
  oktaClientId?: string;
  oktaSecretKey?: string;
};

export type IBoolSSOMandate = {
  enabled: boolean;
};

export type ICreateMandateSSORequest = {
  value: IBoolSSOMandate;
};

export type ICreateMSSourceRequest = {
  enabled: boolean;
};

export type ICreateGoogleSourceRequest = {
  enabled: boolean;
};

export type IMSSource = {
  enabled?: boolean;
};

export type IMandateSSOSource = {
  enabled?: boolean;
};

export type IGoogleSource = {
  enabled?: boolean;
};

export type IOneloginUrl = {
  redirect?: string;
};

export type IOktaLoginUrl = {
  loginUrl?: string;
};

// Labels
export type ILabelSetting = {
  id: number;
  name: string;
  type: LabelEntityType;
  rule: IRule | null;
  children?: ILabelSetting[] | null;
};

// Notifications
export interface INotification<Details extends IEdgeAuditLog = IEdgeAuditLog> {
  id: number;
  createdOn: number;
  markedReadOn: number | null;
  details: Details;
  reason: string;
}

export interface INotificationConfig {
  watchedStatuses?: string;
  watchedLabels: {
    [EntityType.PRODUCT]?: ILabel[];
    [EntityType.FEATURE]?: ILabel[];
    [EntityType.THEME]?: ILabel[];
  };
}

// OAuth Providers

export enum OAuthStatus {
  AUTHENTICATED = "AUTHENTICATED",
  UNAUTHENTICATED = "UNAUTHENTICATED"
}

export enum IJiraOnPremStatus {
  AUTHENTICATED = "AUTHENTICATED",
  UNAUTHENTICATED = "UNAUTHENTICATED"
}

export interface IProviderSource {
  id: number;
  name: string;
  providerUserLogin: string;
  oauthStatus: OAuthStatus;
  oauthClientId: string;
  oauthClientSecret: string;
  type: string;
  configuredBaseUrl?: string;
}

export interface IBusinessDataSource extends IProviderSource {
  canBeATicketSource: boolean;
  type: BusinessDataSourceType;
  instanceName?: string;
}

// Phrases

export interface IPhrase {
  id: number;
  ruleId: number;
  value: string;
}

export interface IPhraseSetting {
  phrase: IPhrase;
  label: ILabelSetting;
}

// Products

export interface IProduct extends IEntity {
  features: IFeature[];
  rule: IRule | null;
}

// Product Issues

export interface IProductIssue {
  id: number;
  externalId: string;
  urlReference: { id: number; url: string } | null;
}

export interface IProductIssueDetails {
  status: {
    id: string;
    name: string;
  };
  created: string;
  creator: {
    key: string;
    name: string;
    avatarUrls: {
      "16x16": string;
      "24x24": string;
      "32x32": string;
      "48x48": string;
    };
    displayName: string;
    emailAddress: string;
  };
  summary: string;
  updated: string;
  assignee: {
    key: string;
    name: string;
    avatarUrls: {
      "16x16": string;
      "24x24": string;
      "32x32": string;
      "48x48": string;
    };
    displayName: string;
    emailAddress: string;
  } | null;
  priority: {
    id: string;
    name: string;
  };
  issuetype: {
    id: string;
    name: string;
    description: string;
  };
  description: string;
}

export interface IProductIssueDetailed extends IProductIssue {
  details: IProductIssueDetails | null;
}

// Rules

export enum RuleType {
  PRODUCT = "product_rule",
  FEATURE = "feature_rule",
  THEME = "theme_rule"
}

export interface IRule {
  type: string;
  id: number;
  phrases: IPhrase[];
  entity: number;
}

// Templates

export enum TemplateLocation {
  EDGE_DESCRIPTION = "EDGE_DESCRIPTION",
  TICKET_DESCRIPTION = "TICKET_DESCRIPTION"
}

export type ITemplate = {
  id: number;
  version?: string; // Indicates the version of the *text editor*. Currently "markdown.0.1".
  content: string; // e.g. "## Hello, this is a markdown template"
  location: TemplateLocation; // enum value for where this template should be used.
};

// Themes

export interface ITheme extends IEntity {
  common: boolean;
  rule: IRule | null;
}

// Tickets

export interface ITicketSource extends IProviderSource {
  type: TicketSourceType;
  instanceName: string;
  brandNameFilter?: string;
  ticketFormNameFilter?: string;
  appToken?: string;
  scope?: string;
  username?: string;
  password?: string;
}

export enum TicketSourceType {
  SALESFORCE = "SALESFORCE",
  ZENDESK = "ZENDESK",
  SERVICENOW = "SERVICENOW",
  MOCK = "MOCK"
}

export interface ICustomFieldMapping {
  id: number;
  fieldName: string;
  displayName: string;
  displayValue?: string;
}

export interface ITicketFieldValue {
  name: string;
  value: string;
}

export interface ITicketEdgeMetadata {
  edgeId: number;
  filterId: number;
  userId?: number;
  issueId?: string;
  urlReferenceId?: string;
  linkedOn?: number;
}

export interface ITicketListing {
  id: number;
  subject: string;
  externalId: string;
  displayId: string;
  url: string;
  // -1 means ticket is still open
  closedOn: number;
  createdOn: number;
  updatedOn: number;
  priority: string;
  status: string;
  customer: Pick<ICustomer, "id" | "name"> | null;
  products: Pick<IProduct, "id" | "name">[];
  features: Pick<IFeature, "id" | "name">[];
  themes: Pick<ITheme, "id" | "name">[];
  space?: string;
  ticketType?: string;
  reason?: string;
  description: string;
  summary: IRichTextContent | null;
  requesterUser: ITicketUser | null;
  ticketSource: number;
  frustration: ITicketFrustration | null;
  urlReferences: IUrlReferenceShort[] | null;
  edges: number[];
  assigneeUser: ITicketUser | null;
  edgesMetadata: ITicketEdgeMetadata[];
  preview?: string;
  body?: string;
  offset: ResultOffsetType;
  language: string;
  ticketSourceType?: string;
  score_details?: ScoreDetails;
}

export interface ITicketInfo extends ITicketListing {
  comments: ITicketComment[];
  attachments: IDocumentResult[];
  fields: ITicketFieldValue[] | null;
  monetaryValue: number | null;
}

export interface ITicketComment {
  frustration: ITicketFrustration | null;
  plainText: string;
  htmlText: string | null;
  timestamp: number;
  fromTicketUser: ITicketUser | null;
  channel: string | null;
  internal: boolean;
  id: number;
  externalId: string;
  ticket: number;
}

export interface ITicketUser {
  id: number;
  email: string;
  name: string;
}

export interface ITicketFrustration {
  id: number;
  score: number;
  text: string;
  ticket: number;
}

export interface ITicketSearchHit {
  ticketId: string;
  matchedText?: string;
  matchedTextUrl?: string;
  status: string;
  ticketUrl: string;
}

export interface ITicketSearchResult {
  query: string;
  hits: ITicketSearchHit[];
  totalCount: number;
}

export interface IIssueListing {
  id: string;
  title: string;
  url: string;
  displayUrl: string;
  description?: string;
  createdOn?: number;
  updatedOn?: number;
  sourceType: ISSUES_SOURCE_TYPE;
  offset: ResultOffsetType;
  externalId?: string;
  sourceId?: string;
  details?: Record<string, unknown>;
  language: string;
  preview: string;
  score_details?: ScoreDetails;
}

export interface ISocialResult {
  sourceType: "slack" | "microsoft-teams";
  createdOn: number;
  channel: string;
  displayUrl: string;
  id: string;
  parentExternalId?: string;
  preview: string;
  title: string;
  type: "social";
  updatedOn: number;
  url: string;
  body?: string;
  offset: ResultOffsetType;
  language: string;
  score_details?: ScoreDetails;
}
interface ISocialInfo {
  sourceType: "slack" | "microsoft-teams";
  createdOn: number;
  channel: string;
  displayUrl: string;
  id: string;
  updatedOn: number;
  url: string;
  body: string;
  name: string;
}
export interface ISocialComment extends ISocialInfo {
  comments: ISocialInfo[];
}
export interface IOriginalTimeSeriesData {
  date: number;
  total: number;
  linked: number;
}

export interface ITimeSeriesData {
  date: DateTime;
  total: number;
  linked: number;
}

export interface IMetricData {
  date: number;
  total: number;
  linked: number;
}

export enum TicketStatus {
  OPEN = "Open",
  CLOSED = "Closed"
}

export interface IArticle {
  id: string;
  title: string;
  spaceName?: string;
  breadcrumb: string[];
  url: string;
  displayUrl: string;
  sourceType: ARTICLE_SOURCE_TYPE;
  preview: string;
  createdOn?: number;
  updatedOn?: number;
  body?: string;
  offset: ResultOffsetType;
  language: string;
  score_details?: ScoreDetails;
  attachments?: IDocumentResult[];
}

type GeneratedKnowledgeBody = {
  body: string;
};

export interface IGeneratedKnowledgeListing {
  id: string | number;
  title: string;
  body: GeneratedKnowledgeBody;
  createdOn: number;
  updatedOn: number;
  preview: string;
  sourceType: GENERATED_KNOWLEDGE_SOURCE_TYPE;
  url: string;
  offset: ResultOffsetType;
  ticketIds: GeneratedKnowledgeTicketId[];
  sources?: GkSource[];
  score_details?: ScoreDetails;
  authorEmail?: string;
  status: ArticleStatus;
  language: string;
}

export interface IFederated {
  id: string;
  title: string;
  url: string;
  displayUrl: string;
  thumbnailUrl: string;
  sourceType: FEDERATED_SOURCE_TYPE;
  preview: string;
  createdOn?: number;
  updatedOn?: number;
  body?: string;
  offset: ResultOffsetType;
  language: string;
  score_details?: ScoreDetails;
}

export interface IAnswerAIFeedback {
  id: string | number;
  title: string;
  body: string;
  answerId: string;
  url: string;
  displayUrl: string;
  sourceType: string;
  updatedOn: number;
  offset: ResultOffsetType;
  language: string;
  preview?: string;
  score_details?: ScoreDetails;
}

export interface IDocument {
  id: string;
  title: string;
  url: string;
  displayUrl: string;
  // Path to the backend endpoint which serves the document itself, which is what previews
  // embed; the endpoint redirects to a GCS URL it signs on demand. `displayUrl` points at the
  // source system instead, which generally can't be rendered in an iframe. Null when we have
  // no viewable copy of the document in GCS.
  displayName: string | null;
  sourceType: DOCUMENT_SOURCE_TYPE;
  preview: string;
  sourceId: string;
  createdOn?: number;
  updatedOn?: number;
  body?: string;
  offset: ResultOffsetType;
  language: string;
  score_details?: ScoreDetails;
}

export interface IUrlReferenceShort {
  id: number;
  domain: string;
  url: string;
  productIssueId?: string;
}

// URL Reference (to Jira)
export interface IUrlReference extends IUrlReferenceShort {
  ticketUrls: Array<{
    id: string;
    url: string;
  }>;
}

export interface IArticleSetting {
  value: string;
}

// Saved Queries

export enum SavedQueryType {
  CREATED_TICKETS = "CREATED_TICKETS",
  OPEN_TICKETS = "OPEN_TICKETS",
  EDGE_LISTING = "EDGE_LISTING",
  CREATED_EDGES = "CREATED_EDGES",
  EDGE_AUTOLINKING = "EDGE_AUTOLINKING"
}

export interface ISavedQuery {
  id: number;
  type: SavedQueryType;
  query: string;
  name: string;
  pinned: boolean;
  updatedOn: number;
  createdOn: number;
  reply?: string;
  replyDisabled?: boolean;
  replyInternal?: boolean;
}

// User

export interface IUser {
  id: number;
  email: string;
  admin: boolean;
  accountName: string;
  onboardingComplete: boolean;
}

export enum UserRole {
  ADMIN = "Admin",
  REVIEWER = "Reviewer",
  AGENT = "Agent"
}

export interface IUserListing {
  id: number;
  email: string;
  banned?: boolean;
}

// Query

export enum StatusQuery {
  ALL = "all",
  OPEN = "open",
  CLOSED = "closed"
}

// Team

export interface ITeam {
  id: number;
  name: string;
  edgeCount: number;
}

export interface ISpace {
  name: string;
}

export interface ISlackSocialChannelInfoRequest {
  channels: string[];
}

export interface IMSTeamsSocialChannelInfoRequest {
  groupUrl: string;
  shouldShowChannels: boolean;
}

export interface ISharepointDriveSiteInfoRequest {
  sitePath: string;
  siteDomain: string;
}

export enum ISlackStatus {
  AUTHENTICATED = "AUTHENTICATED",
  UNAUTHENTICATED = "UNAUTHENTICATED"
}

export interface ISlackChannel {
  channelId: string;
  error?: string;
  channelName?: string;
  isMember?: boolean;
  isPrivate?: boolean;
}
export interface ISlackSocialSource {
  appId: string;
  accessToken: string;
  channels?: ISlackChannel[];
  id: number;
  status: ISlackStatus;
}

export interface IMSTeamsSource {
  id: number;
  instanceName: string;
  name: string;
  providerUserLogin: boolean;
  status: string;
  tenantId: string;
  type: string;
  filters:
    | [
        {
          channels: [
            {
              channelId: string;
              channelName: string;
              description: string;
              isPrivate: boolean;
              isSelected: boolean;
            }
          ];
          error?: string;
          description?: string;
          isPrivate?: boolean;
          groupId?: string;
          groupName?: string;
        }
      ]
    | [];
}

export interface IMSTeamsChannels {
  channelId: string;
  channelName: string;
  description: string;
  isPrivate: boolean;
  isSelected: boolean;
}

export interface IMSTeamsGroup {
  channels?: IMSTeamsChannels[] | undefined;
  error?: string;
  description?: string;
  isPrivate?: boolean;
  groupId?: string;
  groupName?: string;
}

export interface ISlackChannelUpdateSource {
  channels: string[];
}

export interface IMSTeamsChannelUpdateSource {
  tenantId: string;
  filters: [];
}

export interface ISharepointDriveSiteUpdateSource {
  sites: string[];
}

export enum IMSTeamsFlags {
  MUTATE = "mutate",
  SELECT = "select",
  CHECK = "check",
  DELETE = "delete",
  LOAD = "load",
  RELOAD = "reload"
}

export enum ISearchFilter {
  ARTICLE = "articles",
  EDGES = "edges",
  ISSUES = "issues",
  TICKETS = "tickets",
  SOCIAL = "social",
  ALL = "all",
  GENERATED_KNOWLEDGE = "generated_knowledge",
  NO_ALL = ""
}
export enum ISearchResultSortingModes {
  RELEVANCE = "relevance",
  CREATED_DATE = "createdDate",
  UPDATED_DATE = "updatedDate"
}

export enum INTEGRATION_STATUS {
  OK = "ok",
  ERROR = "error",
  TIME_OUT = "timeOut"
}

export type IntegrationStatus = "ok" | "error" | "timeOut";

export interface ImageUploadResult {
  success: boolean;
  name?: string;
  url?: string;
}

export interface ISalesforceKnowledgeArticleSourceRequest {
  fromBusinessDataSource: number;
}

export interface ISalesforceKnowledgeArticleSourceData {
  id: number;
  instanceName: string;
  name: string;
  providerUserLogin: string;
  salesforceInstanceName: string;
  status: OAuthStatus;
  type: string;
}

export interface ISnowKnowledgeArticleSourceRequest {
  instanceName: string;
  oauthClientSecret: string;
  oauthClientId: string;
}
export interface ISnowKnowledgeArticleSourceData {
  id: number;
  oauthInstanceName: string;
  oauthClientSecret: string;
  oauthClientId: string;
  name: string;
  providerUserLogin: string;
  status: OAuthStatus;
  type: string;
}

export enum ARTICLE_SOURCE_TYPE {
  ZENDESK_KNOWLEDGE = "zendesk-knowledge-base",
  CONFLUENCE = "confluence",
  SALESFORCE_KNOWLEDGE = "salesforce-knowledge",
  NOTION = "notion",
  SHAREPOINT_PAGES = "sharepoint",
  SERVICENOW_KNOWLEDGE = "servicenow-knowledge",
  DOC360 = "website-document360",
  MADCAP_FLARE = "website-madcap-flare",
  CX_ONE_EXPERT = "website-cxone-expert",
  DECAP_CMS = "website-decap-cms",
  DOCUSAURUS = "website-docusaurusssg",
  FLUID_TOPICS = "website-fluidtopics",
  CLICKHELP = "website-click-help",
  GITHUB = "website-github",
  HUBSPOT = "website-hubspot-cms",
  LITIUM_CMS = "website-litium-cms",
  PROPROFS_KNOWLEDGE_BASE = " website-pro-profs-knowledge-base",
  PUBLIC_ZENDESK_KNOWLEDGE = "website-public-zendesk-knowledge",
  VERINT_COMMUNITY = "website-verint-community",
  ZOOMIN = "website-zoomin"
}

export enum FEDERATED_SOURCE_TYPE {
  FEDERATED = "federated"
}

export enum GENERATED_KNOWLEDGE_SOURCE_TYPE {
  GENERATED_KNOWLEDGE = "generated_knowledge"
}

export enum DOCUMENT_SOURCE_TYPE {
  SHAREPOINT_DRIVE = "sharepointdrive",
  EDGE = "edge",
  SALESFORCEFILES = "salesforcefiles",
  GOOGLE_DRIVE = "website-google-drive"
}

export interface IAzureDevopsIssueSourceRequest {
  organization: string;
  accessToken: string;
}

export interface IAzureDevopsIssueSource {
  id: number;
  organization: string;
  accessToken: string;
  instanceName: string;
  name: string;
  providerUserLogin: string;
  status: OAuthStatus;
  type: string;
}

export interface INotionSourceRequest {
  accessToken: string;
}

export interface INotionSource {
  id: number;
  organization: string;
  accessToken: string;
  instanceName: string;
  name: string;
  providerUserLogin: string;
  status: OAuthStatus;
  type: string;
}

export enum ISSUES_SOURCE_TYPE {
  JIRA = "jira",
  AZURE_DEVOPS = "azure_devops"
}

export enum SOCIAL_SOURCE_TYPE {
  SLACK = "slack",
  MS_TEAMS = "microsoft-teams"
}

export interface IAzureDevopsIssuesSourceRequest {
  organization: string;
  accessToken: string;
}

export type ICreateAzureDevopsIssuesSourceRequest =
  IAzureDevopsIssuesSourceRequest & { id?: number };

export interface IAzureDevopsIssuesSource {
  id: number;
  organization: string;
  accessToken: string;
  instanceName: string;
  name: string;
  providerUserLogin: string;
  status: OAuthStatus;
  type: string;
}

export interface INotionSourceRequest {
  accessToken: string;
}

export interface INotionSource {
  id: number;
  organization: string;
  accessToken: string;
  instanceName: string;
  name: string;
  providerUserLogin: string;
  status: OAuthStatus;
  type: string;
}

export interface IAzureDevopsIssuesSource {
  id: number;
  organization: string;
  accessToken: string;
  instanceName: string;
  name: string;
  providerUserLogin: string;
  status: OAuthStatus;
  type: string;
}

export interface IUserListData {
  admin: boolean;
  banned: boolean;
  email: string;
  id: number;
  role: UserRole;
}
export interface IUserListingResponse {
  hasNext: boolean;
  items: IUserListData[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}
export interface IAdminPasswordResetResponse {
  url: string;
}

export type UserSupport = {
  userId: number;
  userEmail: string;
  accountName: string;
  prettyAccountName: string;
  onboardingComplete: boolean;
  selfServiceToken: string;
  role: UserRole;
};

export type NameAndIdSupport = {
  id: number;
  name: string;
};

export type UrlReferenceSupport = {
  id: number;
  domain: string;
  productIssueId?: string;
  url: string;
};

export type LinkSupport = {
  label?: string;
  url: string;
  urlReference: number;
};

type TicketUserSupport = {
  email: string;
  name: string;
};

export type CookedTicket = {
  id: number;
  externalId: string;
  displayId: string;
  subject: string;
  description: string;
  summary?: IRichTextContent;
  preview: string;
  edges?: number[];
  themes: NameAndIdSupport[];
  products: NameAndIdSupport[];
  features: NameAndIdSupport[];
  customer: { name: string };
  status?: string;
  createdOn: number;
  updatedOn: number;
  frustration: ITicketFrustration | null;
  urlReferences: UrlReferenceSupport[] | null;
  assigneeUser: TicketUserSupport | null;
  url?: string;
  offset: ResultOffsetType;
  language: string;
  ticketSourceType?: string;
};

export type TemplateSupport = {
  id: number;
  version: string; // Indicates the version of the *text editor*. Currently "markdown.0.1".
  content: string; // e.g. "## Hello, this is a markdown template"
  location: TemplateLocation; // enum value for where this template should be used.
};

export enum SearchTypeSupport {
  ALL = "all",
  EDGES = "edges",
  TICKETS = "tickets",
  ARTICLES = "articles",
  DOCUMENT = "document",
  ISSUES = "issues",
  SOCIAL = "social"
}

export interface IDropdownMenuSupport {
  id: string;
  label: string;
  key: string;
  Icon: React.ReactNode;
}

export type PaginatedCompositeSearchResultSupport = {
  items: SearchResultSupport[];
  hasNext: boolean;
  totalPages: number;
  latencyMillis: number;
  timers: Record<string, unknown>;
  page?: number;
  perPage?: number;
  total?: number;
};

export type PaginatedEdgeSearchResultSupport = {
  items: EdgeResultSupport[];
  hasNext: boolean;
  totalPages: number;
  total: number;
};

export type PaginatedTicketSearchResultSupport = {
  items: TicketResultSupport[];
  hasNext: boolean;
  totalPages: number;
  total: number;
};

export type PaginatedArticleSearchResultSupport = {
  items: ArticleResultSupport[];
  hasNext: boolean;
  totalPages: number;
  total: number;
};

export type PaginatedDocumentSearchResultSupport = {
  items: DocumentResultSupport[];
  hasNext: boolean;
  totalPages: number;
  total: number;
};

export type PaginatedIssueSearchResultSupport = {
  items: IssueResultSupport[];
  hasNext: boolean;
  totalPages: number;
  total: number;
};

export type PaginatedSocialSearchResultSupport = {
  items: ISocialSupport[];
  hasNext: boolean;
  totalPages: number;
  total: number;
};

export type EdgeResultSupport = EdgeSupport & { type: "edge" };
export type TicketResultSupport = CookedTicket & { type: "ticket" };
export type ArticleResultSupport = ArticleSupport & { type: "article" };
export type FederatedResultSupport = FederatedSupport & { type: "federated" };
export type DocumentResultSupport = DocumentSupport & { type: "document" };
export type GeneratedKnowledgeResultSupport = GeneratedKnowledgeSupport & {
  type: "generated_knowledge";
};
export type IssueResultSupport = IssueSupport & { type: "issue" };
export type CompositeResultSupport = EdgeResultSupport | TicketResultSupport;

export type SearchResultsSupport = {
  all?: (
    | EdgeResultSupport
    | TicketResultSupport
    | ArticleResultSupport
    | IssueResultSupport
    | ISocialSupport
    | DocumentResultSupport
    | GeneratedKnowledgeResultSupport
  )[];
  edges?: EdgeResultSupport[];
  tickets?: TicketResultSupport[];
  articles?: ArticleResultSupport[];
  documents?: DocumentResultSupport[];
  issues?: IssueResultSupport[];
  social?: ISocialSupport[];
};

export type SearchResultSupport =
  | EdgeResultSupport
  | TicketResultSupport
  | ArticleResultSupport
  | FederatedResultSupport
  | IssueResultSupport
  | ISocialSupport
  | DocumentResultSupport
  | GeneratedKnowledgeResultSupport;

export interface ISocialSupport {
  sourceType: "slack" | "microsoft-teams";
  createdOn: number;
  channel?: string;
  displayUrl: string;
  id: string;
  parentExternalId?: string;
  preview: string;
  title: string;
  type: "social";
  updatedOn?: number;
  url?: string;
  body?: string;
  offset: ResultOffsetType;
  language: string;
}

export type DocumentSupport = {
  id: string;
  title: string;
  url: string;
  displayUrl: string;
  // See `IDocument.displayName`.
  displayName: string | null;
  sourceType: DOCUMENT_SOURCE_TYPE;
  sourceId: string;
  preview: string;
  createdOn?: number;
  updatedOn?: number;
  body?: string;
  offset: ResultOffsetType;
  language: string;
};

export type GeneratedKnowledgeSupport = {
  id: string;
  title: string;
  body: GeneratedKnowledgeBody;
  createdOn: number;
  updatedOn: number;
  preview: string;
  sourceType: GENERATED_KNOWLEDGE_SOURCE_TYPE;
  url: string;
  offset: ResultOffsetType;
  language: string;
};

export type IssueSupport = {
  id: string | number;
  externalId?: string;
  title: string;
  url: string;
  description?: string;
  preview: string;
  created_on?: number;
  createdOn?: number;
  updated_on?: number;
  updatedOn: number;
  displayUrl: string;
  sourceType: ISSUES_SOURCE_TYPE;
  sourceId: string;
  offset: ResultOffsetType;
  language: string;
};

export type ArticleSupport = {
  id: string;
  title: string;
  spaceName?: string;
  breadcrumb: string[];
  url: string;
  preview: string;
  createdOn: number;
  displayUrl: string;
  sourceType: ARTICLE_SOURCE_TYPE;
  updatedOn: number;
  body?: string;
  offset: ResultOffsetType;
  language: string;
  attachments?: IDocumentResult[];
};

export interface FederatedSupport {
  id: string;
  title: string;
  url: string;
  displayUrl: string;
  thumbnailUrl?: string;
  sourceType: FEDERATED_SOURCE_TYPE;
  preview: string;
  createdOn?: number;
  updatedOn?: number;
  body?: string;
  offset: ResultOffsetType;
  language: string;
}

export type EdgeSupport = {
  id: number;
  name: string;
  richSummary: IRichTextContent | null;
  status: string | null;
  tickets?: Pick<CookedTicket, "id" | "externalId">[];
  assignee: { id: number; email: string } | null;
  links?: LinkSupport[];
  archived: boolean;
  preview: string;
  labels: ILabel[];
  updatedOn: number;
  createdOn: number;
  ticketCount?: number;
  url?: string;
  offset: ResultOffsetType;
  language: string;
};

export type ICompositeResultSupport =
  | EdgeResultSupport
  | TicketResultSupport
  | ArticleResultSupport
  | DocumentResultSupport
  | IssueResultSupport
  | ISocialSupport
  | GeneratedKnowledgeResultSupport;

export type RecentSearchItemSupport =
  | IssueSupport
  | ISocialSupport
  | ArticleResultSupport
  | DocumentResultSupport
  | FederatedResultSupport;

export type RecentSearchResultSupport =
  | EdgeResultSupport
  | TicketResultSupport
  | ArticleResultSupport
  | DocumentResultSupport
  | IssueResultSupport
  | ISocialSupport;

export interface SuggestionsResultsSupport {
  suggestions: string[];
}
export interface InsightSearchSuggestionResponse {
  questions: string[];
}
export interface InsightAutoSearchResponse {
  response: "ticket title and descriptions will be returned";
}

export type InsightSummaryUpdate = {
  summary: string;
  timestamp: number;
};
export interface InsightTicketSummaryResponse {
  problemSummary: string;
  sentiment: string;
  updates: InsightSummaryUpdate[];
  timeToResolution: string;
}
export interface InsightCaseLogResponse {
  response: string;
}
export interface InsightNextStepsResponse {
  response: string;
  reasons: string;
  sources: [{ title: string; url: string }];
}

interface FeedbackResults {
  type?: string | null;
  id: string | number;
  title?: string | null;
  preview?: string;
  sourceType?: string;
  final_rank?: number;
  keyword_search_rank?: number;
  semantic_search_rank?: number;
  search_type?: string;
}

export type TopSearchResults = FeedbackResults;

export interface SearchTelemetryEvent {
  search_query: string;
  integration_type?: SearchFilterSubType;
  total_search_results: number;
  top_search_results: TopSearchResults[];
  search_result_position?: number;
  page?: number;
  search_filter?: string;
  search_sub_filter?: string;
}

export type TicketSummaryResult = {
  response: string;
  latencyMillis: number;
};

export type TicketKBArticleResult = {
  response: string;
};

export type SearchFilterSubType =
  | ARTICLE_SOURCE_TYPE
  | SOCIAL_SOURCE_TYPE
  | ISSUES_SOURCE_TYPE
  | "";

export enum APPROVAL_STATUS {
  APPROVAL = "approval",
  DISAPPROVAL = "disapproval"
}

export enum SearchSource {
  TICKET_INSIGHTS = "ticket_insights",
  SEARCH = "search"
}

export interface IDynamicSourceRequest {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface IDynamicIntegrations {
  description: string | null;
  icon_url: string;
  inputs: {
    [key: string]: string | number;
  };
  key: string;
  name: string;
}

export interface IDynamicSource {
  id: string;
  name: string;
  status: OAuthStatus;
  apifyTaskId: string;
  startUrls: string;
  type: "WEBSITE";
  instanceName: string;
  providerUserLogin: boolean;
  key: string;
  inputData: Record<string, string>;
}

export type GeneratedKnowledgeTicketId = {
  external_id: number | string;
  ticket_id: number | string;
  display_id: number | string;
};

export type GkSource = {
  id: string | number;
  displayId: string;
  type: string;
  displayUrl: string;
};

export type ScoreDetails = {
  search_type?: string;
  keyword_es_search_score?: number;
  keyword_search_rank?: number;
  semantic_es_search_score?: number;
  semantic_search_rank?: number;
  final_rank?: number;
  final_similarity_score?: number;
};

export interface ArticleTicketInfo {
  id: GeneratedKnowledgeTicketId;
  articleIds: string[];
  articleCount: number;
  createdOn: number;
  createdBy: string;
}

export interface GeneratedKnowledgeResult {
  id: string;
  title: string;
  body: GeneratedKnowledgeBody;
  authorEmail: string;
  createdOn: number;
  updatedOn: number;
  ticketIds: GeneratedKnowledgeTicketId[];
  status: string;
  reviewers: ArticleReviewer[];
  public: boolean;
  sources: ApiStreamingJSONAnswerSource[];
}

export interface GeneratedKnowledgeListData {
  items: GeneratedKnowledgeResult[];
  grouped_by_status: {
    deprecated?: number;
    draft?: number;
    in_review?: number;
    published?: number;
  };
  hasNext: boolean;
  perPage: number;
  page: number;
  total: number;
  totalPages: number;
  userDrafts: number;
  userReviews: number;
}

export interface ResultType {
  id: string;
  displayId?: string;
  icon: React.ReactElement;
  date: string;
  title: string;
  breadcrumb: string[];
  description?: string;
  updatedOn: number | undefined;
  labels?: ILabel[];
  url: string;
  type: string;
  originalId: string | number;
  sourceType?: string;
  sourceId?: string;
  summary?: IRichTextContent | null;
  externalId?: string;
  language: string;
  preview?: string;
  attachments?: IDocumentResult[];
  // Documents are previewed by embedding the file itself rather than `url`. See
  // `IDocument.displayName`.
  previewPath?: string | null;
}

export type ResultOffsetType = {
  title: Array<[number, number]>;
  body: Array<[number, number]>;
  comment: CommentOffset;
};

export type CommentOffset = {
  [id: string]: Array<[number, number]>;
};

export enum FeedbackMessageState {
  DEFAULT = "default",
  APPROVAL = "approval",
  DISAPPROVAL = "disapproval",
  ADDITIONAL = "additional",
  SUBMIT = "submit"
}

export enum ArticleCreationState {
  GENERATION = "generation",
  GENERATED = "generated"
}

export enum ArticleStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  IN_REVIEW = "in_review",
  DEPRECATED = "deprecated",
  ALL = "all"
}

export enum ArticleStatusLabel {
  DRAFT = "Draft",
  PUBLISHED = "Published",
  IN_REVIEW = "In review",
  DEPRECATED = "Deprecated"
}

export type ArticleReviewer = {
  created_on: number;
  email: string;
  reviewer_id: number;
  status: string;
  updated_on: number;
};

export type ArticleReviewerList = {
  [key: string]: ArticleReviewer[];
};

export type ArticleAvailablePublicly = {
  [key: string]: boolean;
};

export type ArticleStatusBtn = {
  icon: React.ReactElement;
  status: ArticleStatus;
  selected: boolean;
  label: ArticleStatusLabel;
};

export enum ArticleReviewStatus {
  APPROVED = "approved",
  PENDING = "pending"
}

export enum ReviewerApprovalStatus {
  SUCCESS = "success",
  ERROR = "error",
  NONE = "none"
}

export type FeedbackIds = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type AptAnswersFilter = {
  id: string;
  name: string;
  filterType: string;
  filterRules: AptAnswersFilterRule[];
  manageAllSelected?: boolean;
  /** Set on rows saved by the filter builder. */
  ruleTree?: FilterTree | null;
  /** Legacy rows, while the builder flag is on: the same rules as a tree (null: all results). */
  legacyTree?: FilterTree | null;
  conversionWarnings?: string[];
  conversionLossy?: boolean;
};

export type AptAnswersFilterRule = {
  integration: string;
  fieldsStr: string;
  condition: string;
  value: string;
};

/** The filter builder's operators; see backend/services/apt_answers_tree.py. */
export type FilterTreeOperator =
  | "is"
  | "is_not"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "not_starts_with"
  | "is_empty"
  | "is_not_empty"
  | "within_days"
  | "older_than_days";

export type FilterTreeRule = {
  kind: "rule";
  id: string;
  field: string;
  /** Set when the rule checks several fields of one kind; the first is `field`. */
  fields?: string[];
  operator: FilterTreeOperator;
  values: string[];
  /** Shared keyword lists whose values count as this rule's values (KeywordList ids). */
  lists?: string[];
};

/** A named, account-wide list of values that rules can use (Settings › Keyword lists). */
export type KeywordList = {
  id: string;
  name: string;
  values: string[];
  updatedOn: number;
};

export type FilterTreeGroup = {
  kind: "group";
  id: string;
  op: "and" | "or";
  children: FilterTreeNode[];
};

export type FilterTreeNode = FilterTreeRule | FilterTreeGroup;

/** One source's rules in the filter builder: matching rules, or hidden. */
export type FilterTree = {
  v: 1;
  integration: string;
  mode: "match" | "none";
  root?: FilterTreeGroup;
};

export type FilterFieldType =
  | "text_part"
  | "keyword"
  | "list"
  | "text_value"
  | "exact"
  | "date"
  | "unavailable";

export type FilterCatalogField = {
  name: string;
  type: FilterFieldType;
  /** Display name when it differs from `name`, e.g. "Created". */
  label?: string;
  /** Why an unavailable field can't be used. */
  reason?: string;
  valueKind?: "boolean" | "number";
};

/** Typed fields for every configured source. */
export type FilterCatalog = Record<string, FilterCatalogField[]>;

export type AptAnswersIntegrationsObject = {
  name: string;
  fields: string[];
  filter: AptAnswersFilter | AptAnswersNewFilter | undefined;
};

export type AptAnswersFilterRuleUpdate = {
  idx: number;
  key: "fieldsStr" | "condition" | "value";
  value: string;
};

export enum AptAnswersFilterPurpose {
  "SHOW",
  "HIDE"
}

export enum AptAnswersFilterType {
  CSS_FILTER = "css-filter",
  HIDDEN_FILTER = "hidden-filter",
  QUICK_FILTER = "quick-filter",
  PRODUCT_FILTER = "product-filter",
  AFR_TICKETS_FILTER = "afr-tickets-filter",
  AFR_SOURCES_FILTER = "afr-sources-filter"
}

export enum AptAnswersConditions {
  EQUALS = "equals",
  CONTAINS = "contains",
  STARTS_WITH = "startswith",
  NOT_EQUALS = "not-equals",
  NOT_CONTAINS = "not-contains",
  NOT_STARTS_WITH = "not-startswith",
  EMPTY = "empty",
  NOT_EMPTY = "not-empty",

  // We use not-* filters because they're converted to "AND" conditions in the backend, rather than
  // "OR" conditions for positive filters. I.e., if someone is filtering on timestamp, they
  // probably want it to apply to all results, rather than being simply another condition that
  // *can* be met. However, this is potentially confusing, and we should probably reflect this in
  // the UI somehow. (See the docstrings of `_get_clauses_by_integration()` and
  // `_get_clauses_by_integration_tsvector()` in ai/text/ir/query.py for more.)
  GREATER_THAN_DAYS_AGO = "not-lessorequal-daysago",
  LESS_THAN_DAYS_AGO = "not-greaterorequal-daysago"
}

export type TicketDetailsType = {
  assigneeUser: null | {
    id: number;
    name: string;
    email: string;
  };
  caseNumber: null | string;
  channel: string;
  closedOn: number;
  comments: {
    channel: string;
    externalId: string;
    fromTicketUser: {
      email: string;
      id: number;
      name: string;
    };
    frustration: null | string;
    htmlText: string;
    id: number;
    internal: boolean;
    plainText: string;
    ticketSourceId: number;
    timestamp: number;
  }[];
  createdOn: number;
  customer: {
    id: number;
    name: string;
  };
  description: string;
  displayId: string;
  edges: {
    id: number;
    type: string;
    data: string; // Customize based on your actual data structure
  }[];
  edgesMetadata: {
    key: string;
    value: string; // Customize based on your actual metadata structure
  }[];
  escalationLikely: boolean;
  events: {
    id: number;
    type: string;
    timestamp: number;
  }[];
  externalId: string;
  featureModificationTime: number;
  features: {
    name: string;
    status: string;
  }[];
  fields: {
    name: string;
    value: string;
  }[];
  frustration: null | string;
  id: number;
  loadedOn: number;
  monetaryValue: number;
  priority: string;
  productModificationTime: number;
  products: {
    name: string;
    version: string;
  }[];
  reason: null | string;
  requesterUser: {
    email: string;
    id: number;
    name: string;
  };
  status: string;
  subject: string;
  summary: {
    content: string;
    version: string;
  };
  themeModificationTime: number;
  themes: {
    name: string;
    status: string;
  }[];
  ticketSource: number;
  ticketType: null | string;
  updatedOn: number;
  url: string;
  urlReferences: {
    url: string;
    label: string;
  }[];
};

export interface AptAnswersGroupFilter {
  id: string;
  name: string;
}

export type AptAnswersGroupFilterItem = [string, AptAnswersFilter[]];

export type AptAnswersFilterTypeValues = {
  [key: string]: AptAnswersFilter[];
};

export type AptAnswersNewFilter = {
  id: null;
  filterRules: AptAnswersFilterRule[];
  manageAllSelected?: boolean;
};

export type AptAnswersFiltersResponse = {
  "hidden-filter"?: {
    "hidden-filter": AptAnswersFilter[];
  };
  "css-filter"?: {
    "css-filter": AptAnswersFilter[];
  };
  "afr-tickets-filter"?: {
    "afr-tickets-filter": AptAnswersFilter[];
  };
  "afr-sources-filter"?: {
    "afr-sources-filter": AptAnswersFilter[];
  };
  "quick-filter"?: {
    [key: string]: AptAnswersFilter[];
  };
  "product-filter"?: {
    [key: string]: AptAnswersFilter[];
  };
};

export type MyProductsResponse = {
  id: string;
  filterIds: {
    productFilters: string[];
    // Whether to pick products for a question asked without any; not saved means yes.
    autoSelectProductFilters?: boolean;
  };
};

export type MyProductsListItem = {
  id: string;
  name: string;
};

export type AptAnswersSearchContextItem = {
  id: string;
  name: string;
};

export type AptAnswersSearchContext = {
  isLoading: boolean;
  quickFiltersIsError: boolean;
  quickFiltersIsSuccess: boolean;
  quickFiltersIsEmpty: boolean;
  quickFiltersItems: AptAnswersSearchContextItem[];
  productFiltersIsError: boolean;
  productFiltersIsSuccess: boolean;
  productFiltersIsEmpty: boolean;
  productFiltersItems: AptAnswersSearchContextItem[];
};

export enum AptAnswersContextType {
  QUICK_FILTER = "quick-filter",
  PRODUCT_FILTER = "product-filter"
}

export type UseMutationResultType<Args, Response> = UseMutationResult<
  Response,
  unknown,
  Args,
  unknown
>;
export type TicketFields = {
  externalId: string;
  isFilterable: boolean;
  name: string;
};

interface IIssueComment {
  sourceType: ISSUES_SOURCE_TYPE;
  createdOn: number;
  displayUrl: string;
  id: string;
  updatedOn: number;
  url: string;
  body: string;
  authorId: string;
  externalId: string;
  sourceId: string;
  name: string;
}

export interface IIssueInfo extends IIssueListing {
  comments: IIssueComment[];
}

export enum FilterSource {
  SOURCE_FILTER = "source_filter",
  NON_SOURCE_FILTER = "non_source_filter"
}

export type FilterQueryLabels = { name: string; id: string; type: string };

export type SearchResultLoadingByQuestion = {
  [key: string]: boolean;
};

export type ArticleGeneratedByQuestion = {
  [key: number]: string;
};

export type ArticleSourceFilters = {
  label: string;
  selected: boolean;
  type: SEARCH_RESULT_TYPE;
};

export type IsDebugModeEnabledByQuestion = {
  [answerId: string]: {
    [key: number]: boolean;
  };
};

export enum EditorSource {
  ARTICLE = "article",
  SEARCH = "search"
}

type SkillItemResponse = {
  description: string;
  enabled: boolean;
  friendly_name: string;
  icon: IconName;
  json_mode: boolean;
  model: string;
  promoted: boolean;
  query_description: string;
  quick_name: string;
  render_only: boolean;
  skill: boolean;
  template: string;
};

export type SkillResponse = {
  [key: string]: SkillItemResponse;
};

export type AnswerBody = ArticleGeneratedByQuestion;

export type AnswerSources = ApiStreamingJSONAnswerSource[][];

export type ChatHistoryItemResponse = {
  answer_id: string;
  query: string;
  created_on: number;
};

export type ChatHistory = {
  answerId: string;
  query: string;
  createdOn: number;
};
